import { useEffect, useRef, useState } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { resolveCfNode } from '../store/slot-schema';

interface Box { left: number; top: number; width: number; height: number }

const FLASH_MS = 650;

function liveElement(frameId: string, cfNodeId: string): HTMLElement | null {
  const host = document.querySelector(`[data-frame-id="${CSS.escape(frameId)}"]`);
  return host ? (resolveCfNode(host, cfNodeId) as HTMLElement | null) : null;
}

/** A brief fading accent box over the region a tool step just touched. Reads the
 *  ephemeral `flashRegion` store field, tracks the live element's rect each frame,
 *  fades out over FLASH_MS, then clears itself. Single active flash (new replaces old). */
export function FlashOverlay() {
  const flash = useCanvasStore((s) => s.flashRegion);
  const setFlash = useCanvasStore((s) => s.setFlashRegion);
  const [box, setBox] = useState<Box | null>(null);
  const raf = useRef(0);

  useEffect(() => {
    if (!flash) { setBox(null); return; }
    const tick = () => {
      const el = liveElement(flash.frameId, flash.cfNodeId);
      if (el) {
        const r = el.getBoundingClientRect();
        setBox({ left: r.left, top: r.top, width: r.width, height: r.height });
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    const timer = setTimeout(() => setFlash(null), FLASH_MS);
    return () => { cancelAnimationFrame(raf.current); clearTimeout(timer); };
  }, [flash, setFlash]);

  if (!flash || !box) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-20">
      <div
        className="absolute rounded-[2px] border-2 border-[var(--color-accent)]"
        style={{
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
          animation: 'cf-flash 650ms ease-out forwards',
        }}
      />
    </div>
  );
}
